/**
 * Harmonic Drift - UI Manager
 * Handles all DOM-based UI interactions
 */

import { instrumentManager } from '../managers/InstrumentManager.js';
import { audioEngine } from '../audio/AudioEngine.js';

class UIManager {
    constructor() {
        // DOM element references
        this.elements = {};
        
        // Callbacks
        this.onStartSession = null;
        this.onPausePhysics = null;
        this.onClearNodes = null;
        this.onReturnToMenu = null;
        this.onDragStart = null;
        this.onSaveNodeSettings = null;
    }

    /**
     * Initialize the UI manager
     */
    init() {
        this.cacheElements();
        this.bindEvents();
    }

    /**
     * Cache DOM element references
     */
    cacheElements() {
        this.elements = {
            // Screens
            startScreen: document.getElementById('start-screen'),
            managerScreen: document.getElementById('manager-screen'),
            
            // Settings inputs
            bpmInput: document.getElementById('bpm-input'),
            bpmVal: document.getElementById('bpm-val'),
            speedInput: document.getElementById('speed-input'),
            speedVal: document.getElementById('speed-val'),
            scaleInput: document.getElementById('scale-input'),
            paletteModeInput: document.getElementById('palette-mode-input'),
            enablePhysics: document.getElementById('enable-physics'),
            batteryMode: document.getElementById('battery-mode'),
            controlledMode: document.getElementById('controlled-mode'),
            staticOptions: document.getElementById('static-options'),
            
            // Buttons
            initBtn: document.getElementById('init-btn'),
            manageBtn: document.getElementById('manage-btn'),
            backToStart: document.getElementById('back-to-start'),
            addInstBtn: document.getElementById('add-inst-btn'),
            cancelEditBtn: document.getElementById('cancel-edit-btn'),
            saveInstBtn: document.getElementById('save-inst-btn'),
            deleteInstBtn: document.getElementById('delete-inst-btn'),
            pauseBtn: document.getElementById('pause-btn'),
            clearBtn: document.getElementById('clear-btn'),
            menuBtn: document.getElementById('menu-btn'),
            previewSoundBtn: document.getElementById('preview-sound-btn'),
            
            // HUD
            hud: document.getElementById('hud'),
            entropyDisp: document.getElementById('entropy-disp'),
            scaleDisp: document.getElementById('scale-disp'),
            nodeCountDisp: document.getElementById('node-count-disp'),
            
            // Sidebar
            sidebar: document.getElementById('sidebar'),
            trashZone: document.getElementById('trash-zone'),
            
            // Controls
            controls: document.getElementById('controls'),
            
            // Editor - Basic
            editorForm: document.getElementById('editor-form'),
            editorTitle: document.getElementById('editor-title'),
            editId: document.getElementById('edit-id'),
            editName: document.getElementById('edit-name'),
            editIcon: document.getElementById('edit-icon'),
            editColor: document.getElementById('edit-color'),
            editType: document.getElementById('edit-type'),
            editBehavior: document.getElementById('edit-behavior'),
            
            // Editor - Sound Parameters
            oscRow: document.getElementById('osc-row'),
            noiseRow: document.getElementById('noise-row'),
            editOscType: document.getElementById('edit-osc-type'),
            editNoiseType: document.getElementById('edit-noise-type'),
            
            // Editor - Envelope
            envelopeSection: document.getElementById('envelope-section'),
            editAttack: document.getElementById('edit-attack'),
            editDecay: document.getElementById('edit-decay'),
            editSustain: document.getElementById('edit-sustain'),
            editRelease: document.getElementById('edit-release'),
            attackVal: document.getElementById('attack-val'),
            decayVal: document.getElementById('decay-val'),
            sustainVal: document.getElementById('sustain-val'),
            releaseVal: document.getElementById('release-val'),
            
            // Editor - Modulation (FM/AM)
            modulationSection: document.getElementById('modulation-section'),
            modIndexRow: document.getElementById('mod-index-row'),
            editHarmonicity: document.getElementById('edit-harmonicity'),
            editModIndex: document.getElementById('edit-mod-index'),
            editModType: document.getElementById('edit-mod-type'),
            harmonicityVal: document.getElementById('harmonicity-val'),
            modIndexVal: document.getElementById('mod-index-val'),
            
            // Editor - Membrane/Drum
            membraneSection: document.getElementById('membrane-section'),
            editPitchDecay: document.getElementById('edit-pitch-decay'),
            editOctaves: document.getElementById('edit-octaves'),
            pitchDecayVal: document.getElementById('pitch-decay-val'),
            octavesVal: document.getElementById('octaves-val'),
            
            // Editor - Pluck
            pluckSection: document.getElementById('pluck-section'),
            editDampening: document.getElementById('edit-dampening'),
            editResonance: document.getElementById('edit-resonance'),
            dampeningVal: document.getElementById('dampening-val'),
            resonanceVal: document.getElementById('resonance-val'),
            
            // Editor - Metal
            metalSection: document.getElementById('metal-section'),
            editMetalFreq: document.getElementById('edit-metal-freq'),
            editMetalHarmonicity: document.getElementById('edit-metal-harmonicity'),
            metalFreqVal: document.getElementById('metal-freq-val'),
            metalHarmVal: document.getElementById('metal-harm-val'),
            
            // Lists
            instrumentList: document.getElementById('instrument-list'),
            
            // Misc
            loader: document.getElementById('loader'),
            visualizer: document.getElementById('visualizer'),

            // Node Editor Modal
            nodeEditorModal: document.getElementById('node-editor-modal'),
            nodeEditorForm: document.getElementById('node-editor-form'),
            nodeIdInput: document.getElementById('node-id'),
            nodeFixedCheckbox: document.getElementById('node-fixed'),
            nodeMutedCheckbox: document.getElementById('node-muted'),
            saveNodeBtn: document.getElementById('save-node-btn'),
            cancelNodeBtn: document.getElementById('cancel-node-btn'),
            
            // Help Modal
            helpBtn: document.getElementById('help-btn'),
            helpModal: document.getElementById('help-modal'),
            closeHelpBtn: document.getElementById('close-help-btn'),
        };
    }

    /**
     * Bind event listeners
     */
    bindEvents() {
        // Settings inputs
        this.elements.bpmInput.addEventListener('input', (e) => {
            this.elements.bpmVal.innerText = e.target.value;
        });
        
        this.elements.speedInput.addEventListener('input', (e) => {
            this.elements.speedVal.innerText = e.target.value + 'x';
        });

        this.elements.enablePhysics.addEventListener('change', (e) => {
            this.elements.staticOptions.style.display = e.target.checked ? 'none' : 'block';
        });

        // Start session
        this.elements.initBtn.addEventListener('click', () => {
            if (this.onStartSession) this.onStartSession();
        });

        // Manager navigation
        this.elements.manageBtn.addEventListener('click', () => this.openManager());
        this.elements.backToStart.addEventListener('click', () => this.closeManager());

        // Instrument editor
        this.elements.addInstBtn.addEventListener('click', () => this.openEditor());
        this.elements.cancelEditBtn.addEventListener('click', () => this.closeEditor());
        this.elements.saveInstBtn.addEventListener('click', () => this.saveInstrument());
        this.elements.deleteInstBtn.addEventListener('click', () => this.deleteInstrument());
        
        // Synth type change - show/hide relevant sections
        this.elements.editType.addEventListener('change', () => this.updateEditorSections());
        
        // Slider value displays
        this.bindSliderDisplay(this.elements.editAttack, this.elements.attackVal, 's');
        this.bindSliderDisplay(this.elements.editDecay, this.elements.decayVal, 's');
        this.bindSliderDisplay(this.elements.editSustain, this.elements.sustainVal, '%', 100);
        this.bindSliderDisplay(this.elements.editRelease, this.elements.releaseVal, 's');
        this.bindSliderDisplay(this.elements.editHarmonicity, this.elements.harmonicityVal);
        this.bindSliderDisplay(this.elements.editModIndex, this.elements.modIndexVal);
        this.bindSliderDisplay(this.elements.editPitchDecay, this.elements.pitchDecayVal, 's');
        this.bindSliderDisplay(this.elements.editOctaves, this.elements.octavesVal);
        this.bindSliderDisplay(this.elements.editDampening, this.elements.dampeningVal, 'Hz');
        this.bindSliderDisplay(this.elements.editResonance, this.elements.resonanceVal);
        this.bindSliderDisplay(this.elements.editMetalFreq, this.elements.metalFreqVal, 'Hz');
        this.bindSliderDisplay(this.elements.editMetalHarmonicity, this.elements.metalHarmVal);
        
        // Preview sound button
        this.elements.previewSoundBtn.addEventListener('click', () => this.previewSound());

        // HUD controls
        this.elements.pauseBtn.addEventListener('click', () => {
            if (this.onPausePhysics) this.onPausePhysics();
        });
        
        this.elements.clearBtn.addEventListener('click', () => {
            if (this.onClearNodes) this.onClearNodes();
        });
        
        this.elements.menuBtn.addEventListener('click', () => {
            if (this.onReturnToMenu) this.onReturnToMenu();
        });

        // Node Editor
        this.elements.cancelNodeBtn.addEventListener('click', () => this.closeNodeEditor());
        this.elements.saveNodeBtn.addEventListener('click', () => this.saveNodeSettings());
        
        // Help Modal
        this.elements.helpBtn.addEventListener('click', () => this.openHelp());
        this.elements.closeHelpBtn.addEventListener('click', () => this.closeHelp());
        this.elements.helpModal.addEventListener('click', (e) => {
            if (e.target === this.elements.helpModal) this.closeHelp();
        });
    }
    
    /**
     * Bind slider to display element
     */
    bindSliderDisplay(slider, display, suffix = '', multiplier = 1) {
        if (!slider || !display) return;
        slider.addEventListener('input', () => {
            const val = parseFloat(slider.value) * multiplier;
            display.innerText = (multiplier === 100 ? Math.round(val) : val.toFixed(2)) + suffix;
        });
    }
    
    /**
     * Update which editor sections are visible based on synth type
     */
    updateEditorSections() {
        const type = this.elements.editType.value;
        
        // Hide all type-specific sections first
        this.elements.oscRow.style.display = 'block';
        this.elements.noiseRow.style.display = 'none';
        this.elements.envelopeSection.style.display = 'block';
        this.elements.modulationSection.style.display = 'none';
        this.elements.membraneSection.style.display = 'none';
        this.elements.pluckSection.style.display = 'none';
        this.elements.metalSection.style.display = 'none';
        
        // Show type-specific sections
        switch (type) {
            case 'NoiseSynth':
                this.elements.oscRow.style.display = 'none';
                this.elements.noiseRow.style.display = 'block';
                break;
            case 'FMSynth':
                this.elements.modulationSection.style.display = 'block';
                this.elements.modIndexRow.style.display = 'block';
                break;
            case 'AMSynth':
                this.elements.modulationSection.style.display = 'block';
                this.elements.modIndexRow.style.display = 'none';
                break;
            case 'MembraneSynth':
                this.elements.membraneSection.style.display = 'block';
                break;
            case 'PluckSynth':
                this.elements.envelopeSection.style.display = 'none';
                this.elements.pluckSection.style.display = 'block';
                break;
            case 'MetalSynth':
                this.elements.oscRow.style.display = 'none';
                this.elements.metalSection.style.display = 'block';
                break;
        }
    }
    
    /**
     * Preview the current sound configuration
     */
    async previewSound() {
        const options = this.buildOptionsFromForm();
        const type = this.elements.editType.value;
        
        try {
            // Test configuration first
            const AudioEngine = audioEngine.constructor;
            if (!AudioEngine.testSynthConfig(type, options)) {
                this.showError("Invalid configuration");
                return;
            }
            
            // Create temporary synth for preview
            const Tone = await import('tone');
            await Tone.start();
            
            let synth;
            if (type === 'NoiseSynth') {
                synth = new Tone.NoiseSynth(options).toDestination();
                synth.triggerAttackRelease('8n');
            } else if (type === 'MetalSynth') {
                synth = new Tone.MetalSynth(options).toDestination();
                synth.triggerAttackRelease('8n');
            } else if (type === 'PluckSynth') {
                synth = new Tone.PluckSynth(options).toDestination();
                synth.triggerAttack('C4');
            } else if (type === 'MembraneSynth') {
                synth = new Tone.MembraneSynth(options).toDestination();
                synth.triggerAttackRelease('C2', '8n');
            } else if (type === 'MonoSynth') {
                synth = new Tone.MonoSynth(options).toDestination();
                synth.triggerAttackRelease('C4', '8n');
            } else if (type === 'DuoSynth') {
                synth = new Tone.DuoSynth(options).toDestination();
                synth.triggerAttackRelease('C4', '8n');
            } else if (type === 'AMSynth') {
                synth = new Tone.AMSynth(options).toDestination();
                synth.triggerAttackRelease('C4', '8n');
            } else if (type === 'FMSynth') {
                synth = new Tone.FMSynth(options).toDestination();
                synth.triggerAttackRelease('C4', '8n');
            } else {
                synth = new Tone.Synth(options).toDestination();
                synth.triggerAttackRelease('C4', '8n');
            }
            
            // Dispose after playing
            setTimeout(() => synth.dispose(), 2000);
        } catch (e) {
            console.error('Preview error:', e);
            this.showError("Couldn't preview: " + e.message);
        }
    }
    
    /**
     * Build Tone.js options object from form controls
     */
    buildOptionsFromForm() {
        const type = this.elements.editType.value;
        const options = {};
        
        // Oscillator settings (for most synths)
        if (type !== 'NoiseSynth' && type !== 'MetalSynth') {
            const oscType = this.elements.editOscType.value;
            if (oscType.startsWith('fat')) {
                options.oscillator = { type: oscType, count: 3, spread: 30 };
            } else {
                options.oscillator = { type: oscType };
            }
        }
        
        // Noise settings
        if (type === 'NoiseSynth') {
            options.noise = { type: this.elements.editNoiseType.value };
        }
        
        // Envelope (for most synths)
        if (type !== 'PluckSynth') {
            options.envelope = {
                attack: parseFloat(this.elements.editAttack.value),
                decay: parseFloat(this.elements.editDecay.value),
                sustain: parseFloat(this.elements.editSustain.value),
                release: parseFloat(this.elements.editRelease.value)
            };
        }
        
        // FM/AM specific
        if (type === 'FMSynth' || type === 'AMSynth') {
            options.harmonicity = parseFloat(this.elements.editHarmonicity.value);
            options.modulation = { type: this.elements.editModType.value };
            
            if (type === 'FMSynth') {
                options.modulationIndex = parseFloat(this.elements.editModIndex.value);
            }
        }
        
        // Membrane specific
        if (type === 'MembraneSynth') {
            options.pitchDecay = parseFloat(this.elements.editPitchDecay.value);
            options.octaves = parseFloat(this.elements.editOctaves.value);
        }
        
        // Pluck specific
        if (type === 'PluckSynth') {
            options.dampening = parseFloat(this.elements.editDampening.value);
            options.resonance = parseFloat(this.elements.editResonance.value);
        }
        
        // Metal specific
        if (type === 'MetalSynth') {
            options.frequency = parseFloat(this.elements.editMetalFreq.value);
            options.harmonicity = parseFloat(this.elements.editMetalHarmonicity.value);
            options.envelope = {
                attack: parseFloat(this.elements.editAttack.value),
                decay: parseFloat(this.elements.editDecay.value),
                release: parseFloat(this.elements.editRelease.value)
            };
        }
        
        return options;
    }
    
    /**
     * Populate form controls from options object
     */
    populateFormFromOptions(options, toneType) {
        // Oscillator
        if (options.oscillator?.type) {
            this.elements.editOscType.value = options.oscillator.type;
        }
        
        // Noise
        if (options.noise?.type) {
            this.elements.editNoiseType.value = options.noise.type;
        }
        
        // Envelope
        if (options.envelope) {
            if (options.envelope.attack !== undefined) {
                this.elements.editAttack.value = options.envelope.attack;
                this.elements.attackVal.innerText = options.envelope.attack.toFixed(2) + 's';
            }
            if (options.envelope.decay !== undefined) {
                this.elements.editDecay.value = options.envelope.decay;
                this.elements.decayVal.innerText = options.envelope.decay.toFixed(2) + 's';
            }
            if (options.envelope.sustain !== undefined) {
                this.elements.editSustain.value = options.envelope.sustain;
                this.elements.sustainVal.innerText = Math.round(options.envelope.sustain * 100) + '%';
            }
            if (options.envelope.release !== undefined) {
                this.elements.editRelease.value = options.envelope.release;
                this.elements.releaseVal.innerText = options.envelope.release.toFixed(2) + 's';
            }
        }
        
        // FM/AM
        if (options.harmonicity !== undefined) {
            this.elements.editHarmonicity.value = options.harmonicity;
            this.elements.harmonicityVal.innerText = options.harmonicity.toFixed(1);
        }
        if (options.modulationIndex !== undefined) {
            this.elements.editModIndex.value = options.modulationIndex;
            this.elements.modIndexVal.innerText = options.modulationIndex;
        }
        if (options.modulation?.type) {
            this.elements.editModType.value = options.modulation.type;
        }
        
        // Membrane
        if (options.pitchDecay !== undefined) {
            this.elements.editPitchDecay.value = options.pitchDecay;
            this.elements.pitchDecayVal.innerText = options.pitchDecay.toFixed(2) + 's';
        }
        if (options.octaves !== undefined) {
            this.elements.editOctaves.value = options.octaves;
            this.elements.octavesVal.innerText = options.octaves;
        }
        
        // Pluck
        if (options.dampening !== undefined) {
            this.elements.editDampening.value = options.dampening;
            this.elements.dampeningVal.innerText = options.dampening + 'Hz';
        }
        if (options.resonance !== undefined) {
            this.elements.editResonance.value = options.resonance;
            this.elements.resonanceVal.innerText = options.resonance.toFixed(2);
        }
        
        // Metal
        if (options.frequency !== undefined && toneType === 'MetalSynth') {
            this.elements.editMetalFreq.value = options.frequency;
            this.elements.metalFreqVal.innerText = options.frequency + 'Hz';
        }
        if (options.harmonicity !== undefined && toneType === 'MetalSynth') {
            this.elements.editMetalHarmonicity.value = options.harmonicity;
            this.elements.metalHarmVal.innerText = options.harmonicity.toFixed(1);
        }
    }
    
    /**
     * Reset form to default values
     */
    resetFormDefaults() {
        this.elements.editOscType.value = 'sine';
        this.elements.editNoiseType.value = 'pink';
        
        this.elements.editAttack.value = 0.01;
        this.elements.attackVal.innerText = '0.01s';
        this.elements.editDecay.value = 0.2;
        this.elements.decayVal.innerText = '0.20s';
        this.elements.editSustain.value = 0.5;
        this.elements.sustainVal.innerText = '50%';
        this.elements.editRelease.value = 1.0;
        this.elements.releaseVal.innerText = '1.00s';
        
        this.elements.editHarmonicity.value = 3;
        this.elements.harmonicityVal.innerText = '3.0';
        this.elements.editModIndex.value = 10;
        this.elements.modIndexVal.innerText = '10';
        this.elements.editModType.value = 'sine';
        
        this.elements.editPitchDecay.value = 0.05;
        this.elements.pitchDecayVal.innerText = '0.05s';
        this.elements.editOctaves.value = 4;
        this.elements.octavesVal.innerText = '4';
        
        this.elements.editDampening.value = 4000;
        this.elements.dampeningVal.innerText = '4000Hz';
        this.elements.editResonance.value = 0.9;
        this.elements.resonanceVal.innerText = '0.90';
        
        this.elements.editMetalFreq.value = 200;
        this.elements.metalFreqVal.innerText = '200Hz';
        this.elements.editMetalHarmonicity.value = 5.1;
        this.elements.metalHarmVal.innerText = '5.1';
    }

    /**
     * Get current settings from inputs
     * @returns {Object} Settings object
     */
    getSettings() {
        return {
            bpm: parseInt(this.elements.bpmInput.value),
            speed: parseFloat(this.elements.speedInput.value),
            scale: this.elements.scaleInput.value,
            paletteMode: this.elements.paletteModeInput.value,
            enablePhysics: this.elements.enablePhysics.checked,
            batteryMode: this.elements.batteryMode.checked,
            controlledMode: this.elements.controlledMode.checked
        };
    }

    /**
     * Show loading state
     */
    showLoading() {
        this.elements.initBtn.style.display = 'none';
        this.elements.loader.style.display = 'block';
    }

    /**
     * Hide loading state
     */
    hideLoading() {
        this.elements.initBtn.style.display = 'block';
        this.elements.loader.style.display = 'none';
    }

    /**
     * Transition to playing state
     */
    showPlayingState() {
        this.elements.startScreen.style.opacity = 0;
        
        setTimeout(() => {
            this.elements.startScreen.style.display = 'none';
            this.elements.hud.style.opacity = 1;
            this.elements.sidebar.style.display = 'flex';
            this.elements.controls.style.display = 'flex';
        }, 500);
    }

    /**
     * Transition back to menu
     */
    showMenuState() {
        this.elements.hud.style.opacity = 0;
        this.elements.sidebar.style.display = 'none';
        this.elements.controls.style.display = 'none';
        this.elements.startScreen.style.display = 'flex';

        setTimeout(() => {
            this.elements.startScreen.style.opacity = 1;
            this.elements.initBtn.style.display = 'block';
            this.elements.initBtn.innerText = "Resume Session";
            this.elements.loader.style.display = 'none';
        }, 100);
    }

    /**
     * Update HUD display
     * @param {Object} data - Display data
     */
    updateHUD(data) {
        if (data.entropy !== undefined) {
            this.elements.entropyDisp.innerText = Math.floor(data.entropy * 100) + "%";
        }
        if (data.nodeCount !== undefined) {
            this.elements.nodeCountDisp.innerText = data.nodeCount;
        }
        if (data.scale !== undefined) {
            this.elements.scaleDisp.innerText = data.scale;
        }
    }

    /**
     * Update visualizer progress
     * @param {number} progress - Progress (0-1)
     */
    updateVisualizer(progress) {
        this.elements.visualizer.style.transform = `scaleX(${progress})`;
    }

    /**
     * Toggle physics pause button state
     * @param {boolean} paused - Whether physics is paused
     */
    setPauseButtonState(paused) {
        this.elements.pauseBtn.classList.toggle('active', paused);
    }

    /**
     * Build the sidebar with instrument palette
     * @param {Array} instruments - Array of instrument definitions
     */
    buildSidebar(instruments) {
        this.elements.sidebar.innerHTML = '';
        
        // Extract unique categories from instruments (based on toneType)
        const categories = this.getCategoriesFromInstruments(instruments);
        this.currentCategory = 'all';
        this.allInstruments = instruments;
        
        // Create category tabs container
        const tabsContainer = document.createElement('div');
        tabsContainer.className = 'sidebar-tabs';
        
        // Add "All" tab
        const allTab = document.createElement('button');
        allTab.className = 'sidebar-tab active';
        allTab.setAttribute('data-category', 'all');
        allTab.innerText = 'All';
        allTab.addEventListener('click', () => this.filterByCategory('all'));
        tabsContainer.appendChild(allTab);
        
        // Add category tabs
        categories.forEach(cat => {
            const tab = document.createElement('button');
            tab.className = 'sidebar-tab';
            tab.setAttribute('data-category', cat.id);
            tab.innerText = cat.label;
            tab.addEventListener('click', () => this.filterByCategory(cat.id));
            tabsContainer.appendChild(tab);
        });
        
        this.elements.sidebar.appendChild(tabsContainer);
        
        // Create instruments grid container
        const gridContainer = document.createElement('div');
        gridContainer.className = 'sidebar-grid';
        gridContainer.id = 'sidebar-grid';
        
        instruments.forEach(inst => {
            const el = this.createPaletteItem(inst);
            gridContainer.appendChild(el);
        });
        
        this.elements.sidebar.appendChild(gridContainer);
        
        // Add floating trash zone
        const trashFloat = document.createElement('div');
        trashFloat.id = 'trash-float';
        trashFloat.innerHTML = `<div id="trash-zone">🗑️</div>`;
        this.elements.sidebar.appendChild(trashFloat);
        
        // Re-cache trash zone reference
        this.elements.trashZone = document.getElementById('trash-zone');
    }
    
    /**
     * Create a palette item element for an instrument
     * @param {Object} inst - Instrument definition
     * @returns {HTMLElement} Palette item element
     */
    createPaletteItem(inst) {
        const el = document.createElement('div');
        el.className = 'palette-item';
        el.setAttribute('data-id', inst.id);
        el.setAttribute('data-category', this.getInstrumentCategory(inst));
        el.innerText = inst.icon || inst.name.charAt(0);
        el.style.color = inst.color;
        el.style.borderColor = inst.color;
        el.style.boxShadow = `0 0 5px ${inst.color}`;

        // Tooltip
        const tip = document.createElement('div');
        tip.className = 'palette-tooltip';
        tip.innerText = inst.name;
        el.appendChild(tip);

        // Drag handlers
        el.addEventListener('mousedown', (e) => {
            if (this.onDragStart) this.onDragStart(e, inst.id);
        });
        el.addEventListener('touchstart', (e) => {
            if (this.onDragStart) this.onDragStart(e, inst.id);
        }, { passive: false });
        
        return el;
    }
    
    /**
     * Get categories from instrument list
     * @param {Array} instruments - Array of instruments
     * @returns {Array} Array of category objects
     */
    getCategoriesFromInstruments(instruments) {
        const categoryMap = {
            'synth': { id: 'synth', label: '🎹', types: ['Synth', 'MonoSynth', 'DuoSynth'] },
            'fm': { id: 'fm', label: '📻', types: ['FMSynth', 'AMSynth'] },
            'perc': { id: 'perc', label: '🥁', types: ['MembraneSynth', 'MetalSynth', 'NoiseSynth'] },
            'pluck': { id: 'pluck', label: '🎸', types: ['PluckSynth'] }
        };
        
        const usedCategories = new Set();
        instruments.forEach(inst => {
            for (const [catId, cat] of Object.entries(categoryMap)) {
                if (cat.types.includes(inst.toneType)) {
                    usedCategories.add(catId);
                    break;
                }
            }
        });
        
        return Array.from(usedCategories).map(id => categoryMap[id]);
    }
    
    /**
     * Get category ID for an instrument
     * @param {Object} inst - Instrument
     * @returns {string} Category ID
     */
    getInstrumentCategory(inst) {
        const typeMap = {
            'Synth': 'synth', 'MonoSynth': 'synth', 'DuoSynth': 'synth',
            'FMSynth': 'fm', 'AMSynth': 'fm',
            'MembraneSynth': 'perc', 'MetalSynth': 'perc', 'NoiseSynth': 'perc',
            'PluckSynth': 'pluck'
        };
        return typeMap[inst.toneType] || 'synth';
    }
    
    /**
     * Filter sidebar items by category
     * @param {string} category - Category ID or 'all'
     */
    filterByCategory(category) {
        this.currentCategory = category;
        
        // Update tab active states
        const tabs = this.elements.sidebar.querySelectorAll('.sidebar-tab');
        tabs.forEach(tab => {
            tab.classList.toggle('active', tab.getAttribute('data-category') === category);
        });
        
        // Filter items
        const items = this.elements.sidebar.querySelectorAll('.palette-item');
        items.forEach(item => {
            if (category === 'all' || item.getAttribute('data-category') === category) {
                item.style.display = '';
            } else {
                item.style.display = 'none';
            }
        });
    }

    /**
     * Set trash zone hover state
     * @param {boolean} active - Whether dragging over trash
     */
    setTrashActive(active) {
        if (this.elements.trashZone) {
            this.elements.trashZone.classList.toggle('drag-over', active);
        }
    }

    /**
     * Check if coordinates are over trash zone
     * @param {number} screenX - Screen X coordinate
     * @param {number} screenY - Screen Y coordinate
     * @returns {boolean} Whether over trash
     */
    isOverTrash(screenX, screenY) {
        if (!this.elements.trashZone) return false;
        
        const rect = this.elements.trashZone.getBoundingClientRect();
        const padding = 20;
        
        return screenX >= rect.left - padding &&
               screenX <= rect.right + padding &&
               screenY >= rect.top - padding &&
               screenY <= rect.bottom + padding;
    }

    /**
     * Check if coordinates are over sidebar
     * @param {number} screenX - Screen X coordinate
     * @param {number} screenY - Screen Y coordinate
     * @returns {boolean} Whether over sidebar
     */
    isOverSidebar(screenX, screenY) {
        const rect = this.elements.sidebar.getBoundingClientRect();
        return screenX >= rect.left &&
               screenX <= rect.right &&
               screenY >= rect.top &&
               screenY <= rect.bottom;
    }

    // --- Help Modal ---

    /**
     * Open the help modal
     */
    openHelp() {
        this.elements.helpModal.style.display = 'flex';
    }

    /**
     * Close the help modal
     */
    closeHelp() {
        this.elements.helpModal.style.display = 'none';
    }

    // --- Node Editor ---

    /**
     * Open the node editor modal and populate it with node data.
     * @param {Node} node - The node to edit.
     */
    openNodeEditor(node) {
        if (!node) return;
        this.elements.nodeIdInput.value = node.id;
        this.elements.nodeFixedCheckbox.checked = node.isFixed;
        this.elements.nodeMutedCheckbox.checked = node.isMuted;
        this.elements.nodeEditorModal.style.display = 'flex';
    }

    /**
     * Close the node editor modal.
     */
    closeNodeEditor() {
        this.elements.nodeEditorModal.style.display = 'none';
        this.elements.nodeIdInput.value = '';
    }

    /**
     * Save settings from the node editor modal.
     */
    saveNodeSettings() {
        const nodeId = this.elements.nodeIdInput.value;
        if (!nodeId) {
            this.closeNodeEditor();
            return;
        }

        const settings = {
            isFixed: this.elements.nodeFixedCheckbox.checked,
            isMuted: this.elements.nodeMutedCheckbox.checked,
        };

        if (this.onSaveNodeSettings) {
            this.onSaveNodeSettings(nodeId, settings);
        }

        this.closeNodeEditor();
    }

    // --- Manager Screen ---

    /**
     * Open the instrument manager
     */
    openManager() {
        this.elements.startScreen.style.display = 'none';
        this.elements.managerScreen.style.display = 'flex';
        this.renderManagerList();
    }

    /**
     * Close the instrument manager
     */
    closeManager() {
        this.elements.managerScreen.style.display = 'none';
        this.elements.startScreen.style.display = 'flex';
    }

    /**
     * Render the instrument list in the manager
     */
    renderManagerList() {
        this.elements.instrumentList.innerHTML = '';

        const all = instrumentManager.getAll();
        all.forEach(inst => {
            const div = document.createElement('div');
            div.className = 'list-item';
            if (!inst.id.startsWith('def_')) div.classList.add('custom');
            
            div.innerHTML = `
                <span class="item-icon" style="color:${inst.color}">●</span>
                <span class="item-name">${inst.name} (${inst.toneType})</span>
                <span class="item-type">${inst.id.startsWith('def_') ? 'Default' : 'Custom'}</span>
            `;
            
            if (!inst.id.startsWith('def_')) {
                const actions = document.createElement('div');
                actions.className = 'item-actions';

                const editBtn = document.createElement('button');
                editBtn.innerText = 'Edit';
                editBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.openEditor(inst);
                });

                const removeBtn = document.createElement('button');
                removeBtn.innerText = 'Remove';
                removeBtn.className = 'remove-btn';
                removeBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (confirm(`Are you sure you want to delete "${inst.name}"?`)) {
                        instrumentManager.remove(inst.id);
                        this.renderManagerList();
                    }
                });
                
                actions.appendChild(editBtn);
                actions.appendChild(removeBtn);
                div.appendChild(actions);

                // Still allow clicking the main body to edit
                div.addEventListener('click', () => this.openEditor(inst));
            }
            
            this.elements.instrumentList.appendChild(div);
        });
    }

    /**
     * Open the instrument editor
     * @param {Object|null} inst - Instrument to edit (null for new)
     */
    openEditor(inst = null) {
        this.elements.editorForm.style.display = 'block';

        if (inst) {
            this.elements.editorTitle.innerText = "Edit Instrument";
            this.elements.editId.value = inst.id;
            this.elements.editName.value = inst.name;
            this.elements.editIcon.value = inst.icon;
            this.elements.editColor.value = inst.color;
            this.elements.editType.value = inst.toneType;
            this.elements.editBehavior.value = inst.behavior || 'normal';
            
            // Populate form from options
            this.populateFormFromOptions(inst.options || {}, inst.toneType);
            
            this.elements.deleteInstBtn.style.display = 'block';
        } else {
            this.elements.editorTitle.innerText = "Create Instrument";
            this.elements.editId.value = '';
            this.elements.editName.value = '';
            this.elements.editIcon.value = '';
            this.elements.editColor.value = '#00f3ff';
            this.elements.editType.value = 'Synth';
            this.elements.editBehavior.value = 'normal';
            
            // Reset to defaults
            this.resetFormDefaults();
            
            this.elements.deleteInstBtn.style.display = 'none';
        }
        
        // Update visible sections based on synth type
        this.updateEditorSections();
    }

    /**
     * Close the instrument editor
     */
    closeEditor() {
        this.elements.editorForm.style.display = 'none';
    }

    /**
     * Save the current instrument in the editor
     */
    saveInstrument() {
        const id = this.elements.editId.value || 'cust_' + Date.now();
        const name = this.elements.editName.value || 'Unknown';
        const icon = this.elements.editIcon.value || name.charAt(0);
        const color = this.elements.editColor.value;
        const toneType = this.elements.editType.value;
        const behavior = this.elements.editBehavior.value;
        
        // Build options from form controls
        const options = this.buildOptionsFromForm();

        // Test the synth configuration
        const AudioEngine = audioEngine.constructor;
        if (!AudioEngine.testSynthConfig(toneType, options)) {
            this.showError("Invalid synth configuration. Please adjust your settings.");
            return;
        }

        const newInst = { id, name, icon, color, toneType, behavior, options };

        if (this.elements.editId.value) {
            instrumentManager.update(id, newInst);
        } else {
            instrumentManager.add(newInst);
        }

        this.renderManagerList();
        this.closeEditor();
    }

    /**
     * Delete the current instrument in the editor
     */
    deleteInstrument() {
        const id = this.elements.editId.value;
        if (confirm("Delete this instrument?")) {
            instrumentManager.remove(id);
            this.renderManagerList();
            this.closeEditor();
        }
    }

    /**
     * Show a temporary error message
     * @param {string} message - The message to display
     */
    showError(message) {
        alert(message);
    }
}

// Export singleton instance
export const uiManager = new UIManager();
